import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Instagram, Square, Youtube, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const FORMATS = [
  { id: "story", label: "Story / Reels / TikTok", ratio: "9:16", w: 1080, h: 1920, icon: Instagram },
  { id: "square", label: "Instagram Feed / LinkedIn", ratio: "1:1", w: 1080, h: 1080, icon: Square },
  { id: "landscape", label: "X / Twitter / OG", ratio: "16:9", w: 1200, h: 675, icon: Youtube },
  { id: "linkedin", label: "LinkedIn Cover", ratio: "1.91:1", w: 1200, h: 628, icon: Linkedin },
];

const THEMES = [
  { id: "dark", bg: "#0A0A0A", fg: "#FFFFFF", muted: "#A1A1AA", accent: "#FF4F00" },
  { id: "light", bg: "#FAFAFA", fg: "#0A0A0A", muted: "#71717A", accent: "#FF4F00" },
  { id: "orange", bg: "#FF4F00", fg: "#FFFFFF", muted: "#FFE0D0", accent: "#FFFFFF" },
];

async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function drawCover(ctx, img, x, y, w, h) {
  const ir = img.width / img.height;
  const r = w / h;
  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (ir > r) {
    // image is wider than target — crop sides
    sw = img.height * r;
    sx = (img.width - sw) / 2;
  } else {
    // image is taller — crop top/bottom
    sh = img.width / r;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function wrapText(ctx, text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export function ShareImageDialog({ open, onOpenChange, testimonial, businessName }) {
  const { i18n, t } = useTranslation();
  const en = !i18n.language?.startsWith("pt");
  const [format, setFormat] = useState("story");
  const [theme, setTheme] = useState("dark");
  const [dataUrl, setDataUrl] = useState(null);
  const canvasRef = useRef(null);

  const fmt = FORMATS.find((f) => f.id === format);
  const th = THEMES.find((t) => t.id === theme);

  useEffect(() => {
    if (!open || !testimonial) return;
    let cancelled = false;
    const raf = requestAnimationFrame(async () => {
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;
      canvas.width = fmt.w;
      canvas.height = fmt.h;
      const ctx = canvas.getContext("2d");
      const { w, h } = fmt;
      // Base background
      ctx.fillStyle = th.bg;
      ctx.fillRect(0, 0, w, h);

      // Video testimonials with a poster: use the video frame as full-bleed background
      const isVideo = testimonial.type === "video" && testimonial.has_poster;
      let videoTheme = th;
      if (isVideo) {
        try {
          const poster = await loadImage(`${API}/videos/${testimonial.id}/poster`);
          if (cancelled) return;
          drawCover(ctx, poster, 0, 0, w, h);
          // Vignette + darken for text readability
          ctx.fillStyle = "rgba(0,0,0,0.55)";
          ctx.fillRect(0, 0, w, h);
          const vg = ctx.createLinearGradient(0, 0, 0, h);
          vg.addColorStop(0, "rgba(0,0,0,0.35)");
          vg.addColorStop(0.5, "rgba(0,0,0,0.15)");
          vg.addColorStop(1, "rgba(0,0,0,0.85)");
          ctx.fillStyle = vg;
          ctx.fillRect(0, 0, w, h);
          // Force dark-theme colors on top of the poster (keep the user's accent)
          videoTheme = { bg: "#0A0A0A", fg: "#FFFFFF", muted: "#D4D4D8", accent: th.accent };
        } catch {
          // fallback to normal theme background if poster fails to load
        }
      }
      const activeTh = videoTheme;
      // Subtle accent glow (top right) — skip on video (poster already sets the mood)
      if (!isVideo) {
        const grd = ctx.createRadialGradient(w * 0.9, h * 0.05, 0, w * 0.9, h * 0.05, w * 0.6);
        grd.addColorStop(0, theme === "orange" ? "rgba(255,255,255,0.18)" : "rgba(255,79,0,0.18)");
        grd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, w, h);
      }

      // Padding varies by format
      const isTall = h > w;
      const pad = isTall ? Math.round(w * 0.08) : Math.round(w * 0.06);
      const topY = pad + (isTall ? 80 : 40);

      // Business name top-left (owner brand)
      ctx.fillStyle = activeTh.accent;
      ctx.font = `700 ${isTall ? 36 : 26}px -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
      ctx.textBaseline = "top";
      ctx.fillText((businessName || "").toUpperCase().slice(0, 32), pad, topY);

      // Video play badge (top-right) — signals this was recorded as a video
      if (isVideo) {
        const badgeR = isTall ? 44 : 30;
        const bx = w - pad - badgeR;
        const by = topY + (isTall ? 18 : 10);
        ctx.beginPath();
        ctx.arc(bx, by, badgeR, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.15)";
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#FFFFFF";
        ctx.stroke();
        // Play triangle
        const t2 = badgeR * 0.5;
        ctx.beginPath();
        ctx.moveTo(bx - t2 * 0.4, by - t2 * 0.7);
        ctx.lineTo(bx + t2 * 0.9, by);
        ctx.lineTo(bx - t2 * 0.4, by + t2 * 0.7);
        ctx.closePath();
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();
      }

      // Stars
      const starSize = isTall ? 48 : 34;
      const starY = topY + (isTall ? 60 : 42);
      const rating = Math.max(1, Math.min(5, testimonial.rating || 5));
      ctx.font = `700 ${starSize}px -apple-system, sans-serif`;
      const starFill = theme === "orange" ? "#FFFFFF" : "#FF4F00";
      const starEmpty = theme === "light" ? "#E4E4E7" : theme === "orange" ? "#FFB998" : "#3F3F46";
      let sx = pad;
      for (let i = 0; i < 5; i++) {
        ctx.fillStyle = i < rating ? starFill : starEmpty;
        ctx.fillText("★", sx, starY);
        sx += starSize * 0.9;
      }

      // Quote body
      const bodyText = (testimonial.text || testimonial.transcript || "").trim();
      const preview = testimonial.highlight?.trim() || (bodyText.length > (isTall ? 320 : 220) ? bodyText.slice(0, isTall ? 317 : 217) + "…" : bodyText);
      const quote = `"${preview}"`;
      const bodySize = isTall ? Math.max(46, Math.min(76, Math.round(1200 / Math.sqrt(quote.length)))) : Math.max(30, Math.min(48, Math.round(900 / Math.sqrt(quote.length))));
      ctx.font = `800 ${bodySize}px "Georgia", "Times New Roman", serif`;
      ctx.fillStyle = activeTh.fg;
      const bodyStartY = starY + starSize + (isTall ? 80 : 44);
      const bodyMaxW = w - pad * 2;
      const lines = wrapText(ctx, quote, bodyMaxW);
      lines.forEach((ln, i) => ctx.fillText(ln, pad, bodyStartY + i * bodySize * 1.2));
      const bodyEndY = bodyStartY + lines.length * bodySize * 1.2;

      // Author block (bottom)
      const authorY = h - pad - (isTall ? 240 : 130);
      let avatarX = pad;
      const avatarSize = isTall ? 120 : 72;
      let textStartX = pad;
      if (testimonial.avatar_path) {
        try {
          const img = await loadImage(`${API}/testimonials/${testimonial.id}/avatar`);
          ctx.save();
          ctx.beginPath();
          ctx.arc(pad + avatarSize / 2, authorY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img, pad, authorY, avatarSize, avatarSize);
          ctx.restore();
          ctx.beginPath();
          ctx.arc(pad + avatarSize / 2, authorY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
          ctx.lineWidth = 4;
          ctx.strokeStyle = activeTh.accent;
          ctx.stroke();
          textStartX = pad + avatarSize + 24;
        } catch {
          textStartX = pad;
        }
      }
      ctx.font = `800 ${isTall ? 42 : 30}px -apple-system, sans-serif`;
      ctx.fillStyle = activeTh.fg;
      ctx.fillText(testimonial.author_name || "", textStartX, authorY + (testimonial.avatar_path ? 12 : 0));
      if (testimonial.author_role) {
        ctx.font = `500 ${isTall ? 28 : 20}px -apple-system, sans-serif`;
        ctx.fillStyle = activeTh.muted;
        ctx.fillText(testimonial.author_role, textStartX, authorY + (testimonial.avatar_path ? 62 : 44));
      }

      // Footer: depoia branding
      ctx.font = `700 ${isTall ? 28 : 20}px -apple-system, sans-serif`;
      ctx.fillStyle = activeTh.muted;
      const brand = en ? "collected with depoia." : "coletado com depoia.";
      const brandW = ctx.measureText(brand).width;
      ctx.fillText(brand, w - pad - brandW, h - pad - (isTall ? 30 : 24));

      setDataUrl(canvas.toDataURL("image/png"));
    });
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, testimonial?.id, format, theme]);

  if (!testimonial) return null;

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `testimonial-${testimonial.author_name?.toLowerCase().replace(/\s+/g, "-") || "share"}-${format}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(en ? "Image downloaded!" : "Imagem baixada!");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="share-image-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{en ? "Share testimonial" : "Compartilhar depoimento"}</DialogTitle>
          <DialogDescription>{en ? "Choose a format and theme to create a ready-to-post image." : "Escolha formato e tema para gerar uma imagem pronta para publicar."}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {FORMATS.map((f) => (
            <button
              key={f.id}
              data-testid={`share-format-${f.id}`}
              onClick={() => setFormat(f.id)}
              className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1.5 transition-colors ${format === f.id ? "border-[#FF4F00] bg-orange-50" : "border-zinc-200 hover:border-zinc-400"}`}
            >
              <f.icon size={18} className={format === f.id ? "text-[#FF4F00]" : "text-zinc-500"} />
              <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">{f.label}</span>
              <span className="text-[10px] text-zinc-400">{f.ratio} · {f.w}×{f.h}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 justify-center">
          <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">{en ? "Theme" : "Tema"}</span>
          {THEMES.map((th) => (
            <button
              key={th.id}
              data-testid={`share-theme-${th.id}`}
              onClick={() => setTheme(th.id)}
              className={`w-8 h-8 rounded-full border-2 transition-transform ${theme === th.id ? "border-[#FF4F00] scale-110" : "border-zinc-200"}`}
              style={{ background: th.bg }}
              aria-label={th.id}
            />
          ))}
        </div>

        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 flex items-center justify-center min-h-[300px] max-h-[420px] overflow-hidden">
          {dataUrl ? (
            <img src={dataUrl} alt="preview" className="max-w-full max-h-[400px] object-contain rounded shadow-lg" data-testid="share-preview-img" />
          ) : (
            <span className="text-xs text-zinc-400">{en ? "Rendering..." : "Renderizando..."}</span>
          )}
        </div>
        <canvas ref={canvasRef} style={{ display: "none" }} />

        <div className="flex justify-end">
          <button
            data-testid="share-download-btn"
            onClick={download}
            disabled={!dataUrl}
            className="px-5 py-2.5 bg-[#0A0A0A] text-white rounded-full text-xs font-semibold hover:bg-zinc-800 transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={13} /> {en ? "Download PNG" : "Baixar PNG"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
